/**
 * contract-blocks.ts — tách nội dung điều khoản (chữ thuần) thành các KHỐI để dựng văn bản đúng chuẩn hợp đồng:
 * đoạn thường, gạch đầu dòng, đoạn đánh số "1.1.", tiêu đề bên "BÊN A…", và BẢNG kẻ.
 *
 * Quy ước viết bảng trong nội dung điều khoản (kiểu Markdown, vẫn là chữ thuần — không HTML):
 *   | Giai đoạn | Nội dung | Số buổi |
 *   |---|---|---|
 *   | 0. Khảo sát | Lập bản đồ quy trình | 1 |
 * Bảng cần ≥2 dòng liên tiếp bắt đầu và kết thúc bằng `|` (dòng ngăn `|---|` tuỳ chọn). Ô chứa `|` thì viết `\|`.
 *
 * LƯU Ý: đây là BẢN SAO của ecdp_backend_source/src/modules/platform/contract/contract-blocks.ts (PDF dựng từ bản đó; trang ký của khách dựng
 * bảng từ cùng quy ước) — sửa một nơi phải sửa nơi kia; hai bên dùng chung bộ ca kiểm thử.
 */

export type Block =
  | { type: 'para'; text: string }
  | { type: 'bullet'; text: string }
  | { type: 'num'; num: string; text: string }
  | { type: 'party'; text: string }
  | { type: 'table'; header: string[]; rows: string[][] }

const BULLET_RE = /^(?:[•▪‣◦]|-)\s+(.*)$/u
const NUM_RE = /^(\d+(?:\.\d+)*\.)\s+(.*)$/u
const PARTY_RE = /^BÊN\s+[AB]\b/u
const SEP_RE = /^\|[\s:|-]+\|$/

const isPipeRow = (l: string) => l.length >= 3 && l.startsWith('|') && l.endsWith('|') && !l.endsWith('\\|')

export function splitRow(line: string): string[] {
  const inner = line.slice(1, -1)
  const cells: string[] = []
  let cur = ''
  for (let i = 0; i < inner.length; i++) {
    if (inner[i] === '\\' && inner[i + 1] === '|') { cur += '|'; i++ } else if (inner[i] === '|') { cells.push(cur.trim()); cur = '' } else cur += inner[i]
  }
  cells.push(cur.trim())
  return cells
}

export function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n').map((l) => l.trim())
  const out: Block[] = []
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]
    if (!l) continue
    if (isPipeRow(l)) {
      let j = i
      const rows: string[][] = []
      while (j < lines.length && isPipeRow(lines[j])) { if (!SEP_RE.test(lines[j])) rows.push(splitRow(lines[j])); j++ }
      if (rows.length >= 2) {
        const n = Math.max(...rows.map((r) => r.length))
        const norm = rows.map((r) => Array.from({ length: n }, (_, k) => r[k] ?? ''))
        out.push({ type: 'table', header: norm[0], rows: norm.slice(1) })
        i = j - 1
        continue
      }
    }
    const b = BULLET_RE.exec(l)
    if (b) { out.push({ type: 'bullet', text: b[1] }); continue }
    const n = NUM_RE.exec(l)
    if (n) { out.push({ type: 'num', num: n[1], text: n[2] }); continue }
    if (PARTY_RE.test(l)) { out.push({ type: 'party', text: l }); continue }
    out.push({ type: 'para', text: l })
  }
  return out
}

/** Điều khoản là PHỤ LỤC (tiêu đề bắt đầu "Phụ lục …") → sang trang mới, có khối chữ ký riêng. */
export const isAppendixTitle = (title: string) => /^Phụ\s+lục\b/iu.test(title.trim())

/** "Phụ lục 01. Nội dung X" → { label: 'PHỤ LỤC 01', name: 'Nội dung X' }. */
export function splitAppendixTitle(title: string): { label: string; name: string } {
  const m = /^(Phụ\s+lục\s+[^\s.:–—-]+)\s*[.:–—-]?\s*(.*)$/iu.exec(title.trim())
  return m ? { label: m[1].toUpperCase(), name: m[2].trim() } : { label: title.trim().toUpperCase(), name: '' }
}
